import React, { useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { MetricsLineChart } from './components';
import { ClusterReturnSeries } from './calculation';

// Define which metrics are "higher is better" vs "lower is better"
const METRIC_DEFINITIONS = {
    anova_f: { higherBetter: true, name: 'ANOVA F-Score' },
    kruskal_f: { higherBetter: true, name: 'Kruskal F-Score' },
    silhouette: { higherBetter: true, name: 'Silhouette Score' },
    wasserstein: { higherBetter: false, name: 'Wasserstein Distance' },
    fragmentation: { higherBetter: false, name: 'Fragmentation' },
    davies_bouldin: { higherBetter: false, name: 'Davies-Bouldin Index' },
    cluster_entropy: { higherBetter: false, name: 'Cluster Entropy' },
    max_target_diff: { higherBetter: true, name: 'Max Target Difference' },
    mi_target_return: { higherBetter: true, name: 'MI Target Return' },
    calinski_harabasz: { higherBetter: true, name: 'Calinski-Harabasz Index' }
} as const;

// Default sensible selection - key metrics that are commonly used
const DEFAULT_SELECTED_METRICS = ['silhouette', 'davies_bouldin', 'calinski_harabasz', 'wasserstein'];

type Props = {
    clustering: {
        id: string;
        name: string;
        series: string;
    };
    clusterReturnSeries: ClusterReturnSeries;
};

const ClusteringMetricsChart = ({
    clustering,
    clusterReturnSeries
}: Props) => {
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_SELECTED_METRICS);

    // Get available metrics from the data
    const availableMetrics = React.useMemo(() => {
        if (!clusterReturnSeries.timeseriesData.length) return [];

        const firstValidPoint = clusterReturnSeries.timeseriesData.find(d =>
            d.clusterMetrics && d.clusterMetrics[0]
        );

        if (!firstValidPoint) return [];

        return Object.keys(firstValidPoint.clusterMetrics[0])
            .filter(metricName => metricName in METRIC_DEFINITIONS);
    }, [clusterReturnSeries]);

    const [movingAverageWindow, setMovingAverageWindow] = useState(14);

    const handleMetricChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        const newSelectedMetrics = typeof value === 'string' ? value.split(',') : value;

        // Prevent unselecting all metrics - must have at least one selected
        if (newSelectedMetrics.length > 0) {
            setSelectedMetrics(newSelectedMetrics);
        }
    };

    if (!clusterReturnSeries.timeseriesData.length || !availableMetrics.length) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height="400px">
                <Typography>No metrics data available</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                Metrics – {clustering.name}
            </Typography>

            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Selected Metrics</InputLabel>
                    <Select
                        multiple
                        value={selectedMetrics}
                        onChange={handleMetricChange}
                        input={<OutlinedInput label="Selected Metrics" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip
                                        key={value}
                                        label={METRIC_DEFINITIONS[value as keyof typeof METRIC_DEFINITIONS].name}
                                        size="small"
                                    />
                                ))}
                            </Box>
                        )}
                    >
                        {availableMetrics.map((metricName) => (
                            <MenuItem key={metricName} value={metricName}>
                                {METRIC_DEFINITIONS[metricName as keyof typeof METRIC_DEFINITIONS].name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: 150 }}>
                    <InputLabel>Moving Average</InputLabel>
                    <Select
                        value={movingAverageWindow}
                        label="Moving Average"
                        onChange={(e) => setMovingAverageWindow(e.target.value as number)}
                    >
                        <MenuItem value={1}>No Average</MenuItem>
                        <MenuItem value={7}>7 Days</MenuItem>
                        <MenuItem value={14}>14 Days</MenuItem>
                        <MenuItem value={30}>30 Days</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <MetricsLineChart
                clusterReturnSeries={clusterReturnSeries}
                selectedMetrics={selectedMetrics}
                movingAverageWindow={movingAverageWindow}
            />
        </Box>
    );
};

export default ClusteringMetricsChart;